/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import pick from 'lodash/pick';

import {
  Currency,
  defaultCurrency,
} from '@zapier/currency/src/hooks/useCurrencies';
import {
  formatPrice,
  formatPriceToBeInCurrency,
  RoundingStrategy,
} from '@zapier/currency/src/utils/formatPriceToBeInCurrency';
import { PriceFormattingOptions } from '@zapier/currency/src/utils/formatPriceToBeInCurrency/types';

type PriceProps = {
  /** Price in USD pennies (i.e. hundredths of a USD dollar) */
  children: number;
  /** Currency to show price in */
  currency?: Currency;
  /** Max number of digits to show */
  maxDecimals?: number;
  /** Minimum number of decimals to show */
  minDecimals?: number;
  /** Applies font size to currency symbol only */
  symbolFontSize?: string;
  /** Set to true if we're passing a localized price that requires no calculation */
  isLocalized?: boolean;
  /**
   * For currencies that don't have decimals/fractional units (e.g. JPY), what type of rounding strategy should we use?
   * For example:
   *   - When showing monthly prices for annual plans, we should round UP in favor of the user (price * 12 will be less than what the user actually pays)
   *   - When showing standard monthly prices, use standard rounding practices i.e. round UP for >= .50 and DOWN for < .50
   */
  roundingStrategy?: RoundingStrategy;
};

const priceCss = css({
  alignItems: 'center',
  display: 'inline-flex',
});

type PriceFormattingMeta = Required<
  Pick<PriceProps, 'currency' | 'minDecimals' | 'maxDecimals' | 'isLocalized'>
> &
  Pick<PriceProps, 'roundingStrategy'>;

function getFormattedPrice(price: number, meta: PriceFormattingMeta): string {
  const { isLocalized, currency, roundingStrategy } = meta;

  const formattingOptions: PriceFormattingOptions = {
    ...pick(meta, ['minDecimals', 'maxDecimals']),
    useRelativeRounding: false,
  };

  /**
   * Zero-decimal currencies need their price rounded based on a given strategy
   * They also need to be treated as whole dollars and not cents (avoid division by 100)
   */
  if (currency.zeroDecimal) {
    formattingOptions.isPriceGivenAsCents = !isLocalized;
    formattingOptions.roundingStrategy =
      roundingStrategy ?? RoundingStrategy.default;
  }

  return isLocalized
    ? formatPrice(price, formattingOptions)
    : formatPriceToBeInCurrency(price, currency, false, formattingOptions);
}

/**
 * Displays a price in a user-friendly format
 */
export const Price = ({
  children,
  currency = defaultCurrency,
  maxDecimals = 2,
  minDecimals = 0,
  symbolFontSize = 'inherit',
  isLocalized = false,
  roundingStrategy,
}: PriceProps) => {
  const absPrice = Math.abs(children);
  const isNegative = children < 0;

  const price = getFormattedPrice(absPrice, {
    currency,
    isLocalized,
    maxDecimals,
    minDecimals,
    roundingStrategy,
  });

  return (
    <span css={priceCss}>
      {isNegative && <span>-</span>}
      <span
        style={{
          fontSize: symbolFontSize,
        }}
      >
        {currency.symbol}
      </span>
      <span>
        {`${price} ${currency?.symbol === '$' ? currency.name : ''}`.trim()}
      </span>
    </span>
  );
};
